import PropTypes from 'prop-types'
import { Redirect, useHistory, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import injectRole from '../../../components/HOC/injectRole'

import { TApi, TCompanies, TReports, TRoles, TRoutes, RECORD_TYPES } from '../../../shared/const'
import { blobTypes, downloadFile } from '../../../shared/helpers'
import http from '../../../shared/http'
import { isAlumni } from '../../../shared/alumni'
import JuniorCompanyFirstReporting from '../../../components/views/student/CompanyReports/JuniorCompany/JuniorCompanyFirstReporting'
import JuniorCompanySecondReporting from '../../../components/views/student/CompanyReports/JuniorCompany/JuniorCompanySecondReporting'
import ReportCompact from '../../../components/views/student/CompanyReports/JuniorCompactCompany'
import CompanyReportJuniorBasic from '../../../components/views/student/CompanyReports/JuniorBasicCompany'
import { useEffect, useState } from 'react'
import Loading from '../../../UI/Loading'
import Btn from '../../../UI/Btn'
import TabBar from '../../../UI/TabBar'
import RecordComments from '../../../UI/RecordComments'

import styles from './style.module.scss'

function ReportReview(props) {
	const { id } = useParams()
	const { t } = useTranslation()
	const history = useHistory()
	const [report, setData] = useState({})
	const [isLoading, setLoading] = useState(false)
	// Der Reiter „Kommentare" ist Sache der Verwaltung — eine Lehrkraft sieht
	// den Bericht, aber nicht die Anmerkungen dazu.
	const [tab, setTab] = useState('report')

	const isAdmin = [TRoles.ADMIN, TRoles.FEDERAL_ADMIN].includes(props.role)
	// Alumni (#213) lesen den Bericht wie die Verwaltung, aber ohne Handlungen
	// und ohne den Reiter „Kommentare" (interne Vermerke).
	const readOnlyAdmin = isAlumni(props.role)

	// Beim Jahresbericht liefert der Server zwei Berichte in einem Umschlag
	// (`AnnualReport` und den Halbjahresbericht dazu) — gemeint ist der
	// Bericht, der hier geöffnet wurde.
	const current = report.AnnualReport || report

	const getReports = () => {
		if (props.role === TRoles.TEACHER) return TApi.TEACHER_GET_REPORT(id)
		if (isAdmin || readOnlyAdmin) return TApi.ADMIN_GET_REPORT(id)
	}

	useEffect(() => {
		http.get(getReports(id)).then(({ data }) => setData(data))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	/**
	 * Herunterladen und Zurückweisen kommen aus dem Drei-Punkte-Menü der
	 * Berichtsliste. Beurteilt wird der Bericht aber hier — also gehören die
	 * beiden Handlungen auch hierher, statt zurück in die Liste zu zwingen.
	 */
	const downloadReport = () => {
		const fileName = `Berichte - ${report.Company?.companyType} - ${report.Company?.name} - ${current.type}`
		setLoading(true)
		http.get(TApi.ADMIN_DOWNLOAD_REPORT(id), { responseType: 'arraybuffer' })
			.then(res => downloadFile(fileName, new Blob([res.data], { type: blobTypes.XLS })))
			.finally(() => setLoading(false))
	}

	const rejectReport = () => {
		setLoading(true)
		http.put(TApi.ADMIN_UNSUBMIT_REPORTS(id))
			.then(() => {
				toast.success(t('The report was reopened and the invoice cancelled'))
				history.push(TRoutes.REPORTS_TABLE)
			})
			.finally(() => setLoading(false))
	}

	const renderReport = () => {
		switch (report.Company?.companyType) {
			case TCompanies.JC:
				return report.type === TReports.SEMIANNUAL ? (
					<JuniorCompanyFirstReporting data={report} />
				) : (
					<JuniorCompanySecondReporting data={report} />
				)
			case TCompanies.JCC:
				return <ReportCompact data={report} />
			case TCompanies.JBC:
				return <CompanyReportJuniorBasic data={report} />
			default:
				return <Loading />
		}
	}

	if (![TRoles.ADMIN, TRoles.FEDERAL_ADMIN, TRoles.TEACHER, TRoles.ALUMNI].includes(props.role) || isNaN(Number(id)))
		return <Redirect to={TRoutes.DASHBOARD} />

	// Der Kasten mit den Handlungen und der Bericht stehen untereinander. Ohne
	// diese Hülle wären beide Flex-Kinder der Seite und stünden nebeneinander —
	// die Knöpfe zögen sich dann als schmale Spalte über die ganze Höhe.
	return (
		<div className={styles.root}>
			{isAdmin && current.id && (
				<div className={styles.actions}>
					<Btn
						theme="secondary"
						width={214}
						loading={isLoading}
						disabled={!current.isSubmitted}
						onClick={downloadReport}
					>
						{t('Download report')}
					</Btn>
					<Btn
						theme="secondary"
						width={214}
						loading={isLoading}
						// Auch bei bezahlter Rechnung möglich: Zurückweisen löscht sie
						// nicht mehr, sondern storniert sie (Anforderung #91).
						disabled={!current.isSubmitted}
						onClick={rejectReport}
					>
						{t('Reject report')}
					</Btn>
				</div>
			)}
			{isAdmin && current.id && (
				<TabBar
					className={styles.tabs}
					tabs={[
						{ key: 'report', label: 'Report' },
						{ key: 'comments', label: 'Comments' }
					]}
					active={tab}
					onChange={setTab}
				/>
			)}

			{isAdmin && tab === 'comments' ? (
				<div className={styles.comments}>
					<RecordComments entityType={RECORD_TYPES.REPORT} entityId={current.id} />
				</div>
			) : (
				renderReport()
			)}
		</div>
	)
}

ReportReview.propTypes = {
	role: PropTypes.string.isRequired
}

export default injectRole(ReportReview)
